import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {generateOptions} from './profileFormOptions';
import {FormSection} from '../../../../../../components/FormMaker/types';
import {GenderPronoun, TreatmentPronoun} from '../../../../../../domain/gender';
import {validateCountryCodePhoneNumber, validatePhoneNumber} from '../../../../../../utils/validators';
import {ApplicantProfile} from '../../../types';

export const useBasicInfoSection = (profile: ApplicantProfile): FormSection[] => {
  const {t: tfb} = useTranslation('translation', {keyPrefix: 'applicantProfile.form.basicInfo'});
  const {t: tsh} = useTranslation('translation', {keyPrefix: 'shared'});
  const {t: td} = useTranslation('domain');

  const basicInfo = useMemo(
    () => ({
      firstName: profile.firstName,
      lastName: profile.lastName,
      genderPronoun: profile.genderPronoun,
      treatmentPronoun: profile.treatmentPronoun,
      profileTitle: profile.profileTitle || profile.workExperience.find((w) => w.isCurrent)?.roleName || '',
      mobilePhoneNumber: profile.mobilePhoneNumber,
      location: profile.location || '',
    }),
    [profile]
  );

  return useMemo<FormSection[]>(
    () => [
      {
        type: 'title',
        data: {
          title: tfb('title'),
        },
      },
      {
        type: 'text',
        data: {
          name: 'firstName',
          placeholder: tfb('firstName.placeholder'),
          label: tfb('firstName.label'),
          type: 'text',
          initialValue: basicInfo.firstName,
          schema: Yup.string()
            .min(2, 'Too Short!')
            .max(64, 'Too Long!')
            .required(tsh('formValidation.required', {fieldName: tfb('firstName.label')}) || 'Required'),
        },
      },
      {
        type: 'text',
        data: {
          name: 'lastName',
          placeholder: tfb('lastName.placeholder'),
          label: tfb('lastName.label'),
          type: 'text',
          initialValue: basicInfo.lastName,
          schema: Yup.string()
            .min(2, 'Too Short!')
            .max(64, 'Too Long!')
            .required(tsh('formValidation.required', {fieldName: tfb('lastName.label')}) || 'Required'),
        },
      },
      {
        type: 'select',
        data: {
          name: 'treatmentPronoun',
          placeholder: tfb('treatmentPronoun.placeholder'),
          label: tfb('treatmentPronoun.label'),
          options: generateOptions(TreatmentPronoun, 'treatmentPronounEnum', td),
          initialValue: basicInfo.treatmentPronoun,
        },
      },
      {
        type: 'select',
        data: {
          name: 'genderPronoun',
          placeholder: tfb('genderPronoun.placeholder'),
          label: tfb('genderPronoun.label'),
          options: generateOptions(GenderPronoun, 'genderPronounEnum', td),
          initialValue: basicInfo.genderPronoun,
        },
      },
      {
        type: 'text',
        data: {
          name: 'profileTitle',
          label: tfb('position.label'),
          placeholder: tfb('position.placeholder'),
          type: 'text',
          initialValue: basicInfo.profileTitle,
        },
      },
      {
        type: 'text',
        data: {
          name: 'location',
          placeholder: tfb('location.placeholder'),
          label: tfb('location.label'),
          type: 'text',
          initialValue: basicInfo.location,
          schema: Yup.string()
            .min(2, 'Too Short!')
            .max(64, 'Too Long!')
            .required(tsh('formValidation.required', {fieldName: tfb('location.label')}) || 'Required'),
        },
      },
      {
        type: 'text',
        data: {
          name: 'mobilePhoneNumber',
          placeholder: tfb('phoneNumber.placeholder'),
          label: tfb('phoneNumber.label'),
          type: 'tel',
          initialValue: basicInfo.mobilePhoneNumber,
          // We want to allow numbers to start with 00XX or +XX
          schema: Yup.string()
            .test('has-country-code', tfb('phoneNumber.noCountryCode') as string, (value) =>
              validateCountryCodePhoneNumber(value ?? '')
            )
            .test('is-valid-phone-number', tfb('phoneNumber.invalidPhoneNumber') as string, (value) =>
              validatePhoneNumber(value ?? '')
            )
            .required(tsh('formValidation.required', {fieldName: tfb('phoneNumber.label')}) || 'Required'),
        },
      },
    ],
    [basicInfo, td, tfb, tsh]
  );
};
